import {
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Select,
  Tooltip,
  Typography,
  Row,
  Col,
} from "antd";
import { useEffect, useRef, useState } from "react";
import { useMobxStores } from "../stores/stores";
import { observer } from "mobx-react";
import { InfoCircleOutlined, HolderOutlined } from "@ant-design/icons";
import "./CreateLearningGraphModal.css";
import { useWatch } from "antd/es/form/Form";

import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function SortableItem2({ item }) {
  const { id, name, outcome } = item;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    border: "1px solid #ccc",
    marginBottom: "4px",
    borderRadius: "4px",
    padding: "10px 14px",
    backgroundColor: "#ffffff",
    cursor: "grab",
    userSelect: "none",
    opacity: isDragging ? 0.7 : 1,
    boxShadow: isDragging ? "0 4px 10px rgba(0,0,0,0.15)" : "none",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <div>
        <div style={{ fontWeight: 500 }}>{name}</div>
        <div style={{ fontSize: 12, opacity: 0.7 }}>{outcome}</div>
      </div>
    </div>
  );
}

function SortableItem({ item, selected, onToggleSelect }) {
  const { id, name, outcome } = item;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    border: "1px solid #ccc",
    marginBottom: "8px",
    borderRadius: "8px",
    padding: "10px 12px",
    backgroundColor: "#ffffff",
    cursor: "default",
    userSelect: "none",
    opacity: isDragging ? 0.7 : 1,
    boxShadow: isDragging ? "0 4px 10px rgba(0,0,0,0.15)" : "none",
    outline: selected ? "2px solid #2b57ff" : "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={() => onToggleSelect(id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onToggleSelect(id);
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
        <div style={{ fontWeight: 500 }}>{name}</div>
        <div style={{ fontSize: 12, opacity: 0.7 }}>{outcome}</div>
      </div>

      <div
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        style={{
          cursor: "grab",
          padding: "6px 8px",
          borderRadius: 6,
          border: "1px solid #e6e6e6",
          background: "#fafafa",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        title="Drag to reorder"
      >
        <HolderOutlined />
      </div>
    </div>
  );
}

const levels = [
  { label: "Beginner", value: "beginner" },
  { label: "Intermediate", value: "intermediate" },
  { label: "Advanced", value: "advanced" },
  // { label: "Expert", value: "expert" },
];

const CreateLearningGraphModal = ({
  isOpen,
  setIsOpen,
  graphData,
  setEditing,
}) => {
  const { learningGraphStore } = useMobxStores();
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();

  const educationalOutcomes = useWatch("educationalOutcomes", form);
  const materialsByOutcome = useWatch("materialsByOutcome", form);
  const learningTopic = useWatch("learningTopic", form);
  const thematicField = useWatch("thematicField", form);
  const level = useWatch("level", form);

  const timeout = useRef(null);
  const [groupedMaterials, setGroupedMaterials] = useState([]);

  const [itemsN, setItemsN] = useState([]);

  const [activeOutcome, setActiveOutcome] = useState("");
  const [leftQuery, setLeftQuery] = useState("");
  const [midQuery, setMidQuery] = useState("");

  const [midSelectedIds, setMidSelectedIds] = useState(new Set());
  const [rightSelectedIds, setRightSelectedIds] = useState(new Set());

  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(() => {
    const fetchEducationalOutcomesAndMaterials = async () => {
      await learningGraphStore.GetEducationalOutcomesAndMaterials();

      let initialMaterialData = [];
      if (graphData.learning_materials.length > 0) {
        initialMaterialData = graphData.learning_materials.map((material) => {
          const materialFound = learningGraphStore.materials.find(
            (storeMaterial) => storeMaterial.id === material.id,
          );

          return {
            id: materialFound?.id,
            outcome: materialFound?.educational_outcome,
            name: materialFound?.title,
          };
        });
      }

      setItemsN(initialMaterialData.filter((item) => item.id));
    };

    fetchEducationalOutcomesAndMaterials();
  }, [graphData, learningGraphStore]);

  const handleSearch = async (inputValue) => {
    if (inputValue !== "") {
      await learningGraphStore.GetFilteredEducationalOutcome(inputValue);
    } else {
      await learningGraphStore.GetEducationalOutcome();
    }
  };

  const leftOutcomes = (educationalOutcomes || []).filter(Boolean);

  useEffect(() => {
    if (!leftOutcomes.length) {
      setActiveOutcome("");
      setMidSelectedIds(new Set());
      return;
    }

    setActiveOutcome((prev) =>
      leftOutcomes.includes(prev) ? prev : leftOutcomes[0],
    );
    setMidSelectedIds(new Set());
  }, [leftOutcomes.join("|")]);

  const visibleMiddleMaterials = learningGraphStore.materials?.filter(
    (m) => m.educational_outcome === activeOutcome,
  );

  const isInRight = (id) => itemsN.some((x) => x.id === id);

  const toggleMidSelected = (id) => {
    setMidSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const addSelectedFromMiddle = () => {
    if (!midSelectedIds.size) return;

    setItemsN((prev) => {
      const next = [...prev];
      for (const id of midSelectedIds) {
        if (next.some((x) => x.id === id)) continue;
        const m = visibleMiddleMaterials.find((x) => x.id === id);
        if (!m) continue;
        next.push({ id: m.id, name: m.title, outcome: m.educational_outcome });
      }
      return next;
    });

    setMidSelectedIds(new Set());
  };

  const addAllVisibleFromMiddle = () => {
    if (!visibleMiddleMaterials?.length) return;

    setItemsN((prev) => {
      const next = [...prev];
      for (const m of visibleMiddleMaterials) {
        if (next.some((x) => x.id === m.id)) continue;
        next.push({ id: m.id, name: m.title, outcome: m.educational_outcome });
      }
      return next;
    });

    setMidSelectedIds(new Set());
  };

  const removeSelectedFromRight = () => {
    if (!rightSelectedIds.size) return;
    setItemsN((prev) => prev.filter((x) => !rightSelectedIds.has(x.id)));
    setRightSelectedIds(new Set());
  };

  const removeAllFromRight = () => {
    setItemsN([]);
    setRightSelectedIds(new Set());
  };

  const handleUpdate = async () => {
    const ids = itemsN.map((mat) => mat.id);

    const newData = {
      learning_topic: form.getFieldValue("learningTopic"),
      thematic_field: form.getFieldValue("thematicField"),
      learning_material_ids: ids,
      level: form.getFieldValue("level"),
    };

    await learningGraphStore.PutLearningGraph(graphData.id, newData);
    graphData.setEditing();
  };

  const handleCancelUpdate = async () => {
    graphData.setEditing();
  };

  return (
    <Form form={form} layout="vertical" className="create-learning-graph-form">
      <>
        <Row gutter={16}>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              label="Learning Topic"
              name="learningTopic"
              initialValue={graphData.learning_topic}
              rules={[{ required: true }]}
            >
              <Input placeholder="Learning topic" maxLength={100} showCount />
            </Form.Item>
          </Col>

          <Col xs={24} sm={24} md={8}>
            <Form.Item
              label="Thematic Field"
              name="thematicField"
              initialValue={graphData.thematic_field}
              rules={[{ required: true }]}
            >
              <Input placeholder="Thematic field" maxLength={100} showCount />
            </Form.Item>
          </Col>

          <Col xs={24} sm={24} md={8}>
            <Form.Item
              label={
                <span>
                  Level&nbsp;
                  <Tooltip
                    title={
                      <div>
                        <div>Beginner → Understand / Apply</div>
                        <div>Intermediate → Analyze</div>
                        <div>Advanced → Evaluate / Create</div>
                      </div>
                    }
                  >
                    <InfoCircleOutlined style={{ fontSize: "14px" }} />
                  </Tooltip>
                </span>
              }
              name="level"
              initialValue={graphData.level}
              rules={[{ required: true }]}
            >
              <Select placeholder="Level">
                {levels.map((lvl, index) => (
                  <Select.Option key={index} value={lvl.value}>
                    {lvl.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* <Typography>
          To design your learning path, select the target educational outcomes
          and learning materials, then arrange the selected materials in the
          desired order.
        </Typography> */}
      </>

      <div style={{ marginTop: "16px" }}>
        <Button
          type="primary"
          key="button-next"
          style={{ marginRight: "8px", marginBottom: "8px" }}
          onClick={handleUpdate}
        >
          Submit changes
        </Button>
        <Button key="button-cancel" onClick={handleCancelUpdate}>
          Cancel
        </Button>
      </div>
    </Form>
  );
};

export default observer(CreateLearningGraphModal);
